import { MONTH_SHORT, PNL_LINES } from '../constants/wfReportConfig';

const amount = value => value == null ? '-' : Number(value).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export function worksheetPdfRows(data) {
  const months = Array.from({ length: 12 }, (_, i) => data.months.find(m => m.month === `${data.year}-${String(i + 1).padStart(2, '0')}`));
  const columns = [...months.map(m => m?.pnl), data.ytd];
  const rows = [];
  const row = (label, read, kind = 'detail') => rows.push({ kind, values: columns.map(p => p ? read(p) : null), cells: [label, ...columns.map(p => {
    const value = p ? read(p) : null;
    return kind === 'ratio' ? (value == null ? '-' : `${amount(value)}%`) : amount(value);
  })] });
  const section = (key, label) => {
    rows.push({ kind: 'section', cells: [label] });
    PNL_LINES.filter(line => line.section === key).forEach(line => row(line.label, p => p.lines[line.key]));
  };
  section('income', 'INCOME'); row('Total Income', p => p.total_income, 'total');
  section('cogs', 'COST OF GOODS SOLD'); row('Total COGS', p => p.total_cogs, 'total');
  row('Gross Profit', p => p.gross_profit, 'total'); row('Gross Margin %', p => p.gross_margin_pct, 'ratio');
  section('fixed', 'FIXED COST'); row('Total Fixed Cost', p => p.total_fixed, 'total');
  row('Net Profit', p => p.net_profit, 'net');
  row('Marketing %', p => p.marketing_pct, 'ratio'); row('Wages %', p => p.wages_pct, 'ratio'); row('Net Profit %', p => p.net_profit_pct, 'ratio');
  return { months, rows };
}

export async function buildWfWorksheetPdf(data) {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' });
  const { months, rows } = worksheetPdfRows(data);
  const width = doc.internal.pageSize.getWidth(), height = doc.internal.pageSize.getHeight();
  doc.setProperties({ title: `W Factory P&L Worksheet ${data.year}`, subject: 'Yearly profit and loss worksheet', author: 'RunningMen' });
  doc.setFont('helvetica', 'bold'); doc.setFontSize(21); doc.setTextColor(56, 51, 75);
  doc.text(`W Factory | ${data.reportType || 'Profit & Loss'} ${data.year}`, 30, 35);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
  doc.text('Wow Factory Event Space Sdn. Bhd. | January–December and YTD | All amounts in RM', 30, 54);
  doc.setFontSize(8); doc.setTextColor(90, 82, 106);
  doc.text(data.reportType ? 'Targets: blank means not set. Totals require all relevant targets. Differences = actual minus target; percentage differences in percentage points.' : 'Saved worksheet figures. Draft months may change. A dash means no recorded amount; 0.00 is a recorded zero.', 30, 70);
  const numberWidth = (width - 60 - 139) / 13;
  autoTable(doc, {
    startY: 84, margin: { left: 30, right: 30, top: 30, bottom: 34 }, theme: 'grid',
    head: [['Account', ...months.map((month, i) => `${MONTH_SHORT[i]}\n${month?.status === 'final' ? 'FINAL' : month?.status === 'draft' ? 'DRAFT' : month?.status === 'target' ? 'TARGET' : 'NOT ENTERED'}`), data.reportType ? 'Full year' : 'YTD']],
    body: rows.map(row => row.kind === 'section' ? [{ content: row.cells[0], colSpan: 14 }] : row.cells),
    styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 4.5, textColor: [56, 51, 75], lineColor: [229, 223, 240], lineWidth: .35, halign: 'right', valign: 'middle', overflow: 'linebreak' },
    headStyles: { fillColor: [238, 232, 246], textColor: [68, 54, 83], fontStyle: 'bold', fontSize: 8.5 },
    columnStyles: Object.fromEntries(Array.from({ length: 14 }, (_, index) => [index, index === 0 ? { cellWidth: 139, halign: 'left' } : { cellWidth: numberWidth }])),
    didParseCell: cell => {
      if (cell.section !== 'body') return;
      const row = rows[cell.row.index];
      if (cell.column.index === 13) { cell.cell.styles.fillColor = [246, 243, 251]; cell.cell.styles.fontStyle = 'bold'; }
      if (row.kind === 'section') { cell.cell.styles.fillColor = [247, 247, 250]; cell.cell.styles.fontStyle = 'bold'; cell.cell.styles.halign = 'left'; }
      if (row.kind === 'total') { cell.cell.styles.fillColor = [240, 234, 250]; cell.cell.styles.fontStyle = 'bold'; }
      if (row.kind === 'net') {
        cell.cell.styles.fillColor = [237, 245, 239]; cell.cell.styles.textColor = [40, 92, 60]; cell.cell.styles.fontStyle = 'bold';
        if (cell.column.index > 0 && row.values[cell.column.index - 1] < 0) { cell.cell.styles.fillColor = [255, 243, 241]; cell.cell.styles.textColor = [163, 46, 39]; }
      }
    },
    didDrawPage: () => { doc.setFontSize(8); doc.setTextColor(90, 82, 106); doc.text(`W Factory | ${data.year} worksheet | ${data.reportType ? 'Full-year targets require twelve months; differences = actual minus target.' : 'YTD totals recorded months; percentages use total income.'}`, 30, height - 18); },
  });
  return doc;
}
export async function downloadWfWorksheetPdf(data) {
  const doc = await buildWfWorksheetPdf(data);
  doc.save(`WF_PnL_${data.reportType ? data.reportType.replace(/ /g, '_') : 'Worksheet'}_${data.year}.pdf`);
}
